import { Events } from '@/models/events.model'
import { useEventBus } from '@/utils/events'
import * as joint from 'jointjs'
import { EventBus } from '@/event-bus'
import { useTraceDataStore } from '@/stores/traceDataStore'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export function useRegisterContainerCollapse() {
  const rappidContext = injectTyped(ServiceKeys.rappidContext)

  function collapseContainer(cell: joint.dia.CellView | joint.dia.Cell): void {
    let container = cell as any
    if (container.model) {
      container = container.model
    }

    container
      .getEmbeddedCells()
      .filter((artifact: joint.dia.CellView | undefined) => artifact != undefined)
      .forEach((artifact: joint.dia.Cell) => {
        rappidContext.paper.findViewByModel(artifact).setInteractivity(false)
        artifact.set('hidden', true)

        const links = rappidContext.graph.getConnectedLinks(artifact)

        links.forEach((link: joint.dia.Link) => {
          if (link.getSourceElement()?.cid === artifact.cid) {
            link.set('original_source', link.source())
            link.source(container as joint.dia.Cell)
          }

          if (link.getTargetElement()?.cid === artifact.cid) {
            link.set('original_target', link.target())
            link.target(container as joint.dia.Cell)
          }
        })
      })

    container.attr('search/visibility', 'hidden')

    container.resize(container.attributes.original_width, container.attributes.original_height)
    container.set('collapsed', true)
  }

  function expandContainer(cell: joint.dia.CellView | joint.dia.Cell): void {
    let container = cell as any
    if (container.model) {
      container = container.model
    }

    container
      .getEmbeddedCells()
      .filter((artifact: joint.dia.Cell | undefined) => artifact != undefined)
      .forEach((elem: joint.dia.Cell) => {
        elem.set('hidden', false)
      })

    const links = rappidContext.graph.getConnectedLinks(container)

    links.forEach((link: joint.dia.Link) => {
      if (link.getSourceElement()?.cid === container.cid && link.attributes.original_source) {
        link.source(link.attributes.original_source)
        link.set('original_source', undefined)
      }

      if (link.getTargetElement()?.cid === container.cid && link.attributes.original_target) {
        link.target(link.attributes.original_target)
        link.set('original_target', undefined)
      }
    })

    container.attr('search/visibility', 'visible')

    const newHeight = container.attributes.original_height + 70

    container.resize(container.attributes.original_width, newHeight)

    // Special case for no search results
    if (container.getEmbeddedCells().length === 0) {
      container.resize(container.attributes.original_width, 150)
    }

    container.set('collapsed', false)
  }

  function toggleCollapseContainer(event: Events['updateContainerToggleCollapse']): void {
    if (event.artifactType.artifactCount === 0) {
      return
    }

    const traceDataStore = useTraceDataStore()

    rappidContext.paper.freeze()

    if (event.artifactType.loadedPages === 0) {
      // If this condition is true, request the artifact data for this artifact container.
      // This is especially the case for the initial expansion of explorer overview containers.
      // For the view from artifact searches, this should not be executed as loadedPages is undefined.
      EventBus.emit('loadArtifactsFromApiAfterExpand', {
        artifactType: event.artifactType,
        cellView: event.cellView,
      })
    } else if (event.cellView.attributes.collapsed) {
      traceDataStore.toggleArtifactTypeCollapsed(event.artifactType, false)
      expandContainer(event.cellView)
    } else {
      traceDataStore.toggleArtifactTypeCollapsed(event.artifactType, true)
      collapseContainer(event.cellView)
    }

    rappidContext.paper.unfreeze()
  }

  useEventBus('updateContainerToggleCollapse', toggleCollapseContainer)
  useEventBus('collapseContainer', collapseContainer)
  useEventBus('expandContainer', expandContainer)
}
